import { useQuery } from "@tanstack/react-query";
import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type PropsWithChildren,
} from "react";
import { z } from "zod";

import { versionInfoSchema, type VersionInfo } from "src/lib/versionSchema";

interface VersionCheckState {
  currentVersion: string;
  latestVersion: string | null;
  isUpdateAvailable: boolean;
  isDismissed: boolean;
  shouldShowBadge: boolean;
  deployedAt: string | null;
  dismissUpdate: () => void;
}

interface AppMetadataContextValue {
  version: {
    current: string;
    full: string;
    deployedAt: string | null;
  };
  versionCheck: VersionCheckState;
}

const AppMetadataContext = createContext<AppMetadataContextValue | null>(null);

const DISMISSED_VERSIONS_KEY = "app:dismissedVersions";
const LAST_DISMISSED_AT_KEY = "app:lastDismissedAt";

const dismissedVersionsSchema = z.array(z.string());

function getDismissedVersions(): string[] {
  try {
    const stored = localStorage.getItem(DISMISSED_VERSIONS_KEY);

    if (!stored) {
      return [];
    }

    const parsed = JSON.parse(stored);
    const validated = dismissedVersionsSchema.parse(parsed);
    return validated;
  } catch {
    return [];
  }
}

function addDismissedVersion(version: string) {
  try {
    const dismissed = getDismissedVersions();

    if (!dismissed.includes(version)) {
      dismissed.push(version);
      localStorage.setItem(DISMISSED_VERSIONS_KEY, JSON.stringify(dismissed));
    }

    localStorage.setItem(LAST_DISMISSED_AT_KEY, Date.now().toString());
  } catch (error) {
    console.error("Failed to save dismissed version:", error);
  }
}

const MIN_VERSION_AGE_MS = 15 * 60 * 1000; // 15 minutes

function isVersionNewer(
  latestDeployedAt: string | null,
  currentDeployedAt: string | null,
): boolean {
  if (!latestDeployedAt || !currentDeployedAt) {
    return false;
  }

  const latestTime = new Date(latestDeployedAt).getTime();
  const currentTime = new Date(currentDeployedAt).getTime();
  return latestTime > currentTime;
}

function isVersionOldEnough(deployedAt: string | null): boolean {
  if (!deployedAt) {
    return true;
  }

  const deployedTime = new Date(deployedAt).getTime();
  const now = Date.now();
  return now - deployedTime >= MIN_VERSION_AGE_MS;
}

function shouldShowUpdate(
  latestVersion: string,
  currentVersion: string,
  latestDeployedAt: string | null,
  currentDeployedAt: string | null,
): boolean {
  if (latestVersion === currentVersion) {
    return false;
  }

  if (!isVersionNewer(latestDeployedAt, currentDeployedAt)) {
    return false;
  }

  if (!isVersionOldEnough(latestDeployedAt)) {
    return false;
  }

  const dismissed = getDismissedVersions();

  if (dismissed.includes(latestVersion)) {
    return false;
  }

  return true;
}

function shouldShowBadge(
  latestVersion: string,
  currentVersion: string,
  latestDeployedAt: string | null,
  currentDeployedAt: string | null,
): boolean {
  if (latestVersion === currentVersion) {
    return false;
  }

  if (!isVersionNewer(latestDeployedAt, currentDeployedAt)) {
    return false;
  }

  return true;
}

interface AppMetadataProviderProps {
  metadata: {
    version: VersionInfo;
  };
}

export function AppMetadataProvider(
  props: PropsWithChildren<AppMetadataProviderProps>,
) {
  const [isDismissed, setIsDismissed] = useState(false);
  const currentVersion = props.metadata.version.version;

  const versionQuery = useQuery({
    queryKey: ["app-version"],
    queryFn: async (): Promise<VersionInfo> => {
      const response = await fetch("/version.json", {
        cache: "no-store",
        headers: {
          "Cache-Control": "no-cache",
        },
      });

      if (!response.ok) {
        throw new Error("Failed to fetch version");
      }

      const data = await response.json();
      const validated = versionInfoSchema.parse(data);
      return validated;
    },
    initialData: props.metadata.version,
    refetchInterval: 5 * 60 * 1000, // 5 minutes
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
    enabled: currentVersion !== "dev",
    retry: 1,
  });

  const latestVersion = versionQuery.data?.version || null;
  const latestDeployedAt = versionQuery.data?.deployedAt || null;
  const currentDeployedAt = props.metadata.version.deployedAt;
  const hasUpdate = latestVersion !== null && latestVersion !== currentVersion;
  const isUpdateAvailable =
    hasUpdate &&
    shouldShowUpdate(
      latestVersion,
      currentVersion,
      latestDeployedAt,
      currentDeployedAt,
    ) &&
    !isDismissed;
  const showBadge =
    hasUpdate &&
    shouldShowBadge(
      latestVersion,
      currentVersion,
      latestDeployedAt,
      currentDeployedAt,
    );

  function dismissUpdate() {
    if (latestVersion) {
      addDismissedVersion(latestVersion);
      setIsDismissed(true);
    }
  }

  useEffect(() => {
    setIsDismissed(false);
  }, [latestVersion]);

  const value: AppMetadataContextValue = useMemo(() => {
    return {
      version: {
        current: props.metadata.version.version,
        full: props.metadata.version.fullVersion,
        deployedAt: props.metadata.version.deployedAt,
      },
      versionCheck: {
        currentVersion,
        latestVersion,
        isUpdateAvailable,
        isDismissed,
        shouldShowBadge: showBadge,
        deployedAt: versionQuery.data?.deployedAt || null,
        dismissUpdate,
      },
    };
  }, [
    props.metadata.version,
    currentVersion,
    latestVersion,
    isUpdateAvailable,
    isDismissed,
    showBadge,
    versionQuery.data?.deployedAt,
    dismissUpdate,
  ]);

  return (
    <AppMetadataContext.Provider value={value}>
      {props.children}
    </AppMetadataContext.Provider>
  );
}

export function useAppMetadata() {
  const context = useContext(AppMetadataContext);

  if (!context) {
    throw new Error("useAppMetadata must be used within AppMetadataProvider");
  }

  return context;
}

export function useAppVersion() {
  return useAppMetadata().version;
}
